/**
 * Cycle 416 — the visible half of a failed alarm ack.
 *
 * ⚠ WHY A COMPONENT AND NOT A TOAST. The brief is explicit and it is right: a
 * toast can be missed, and a console line is not a user surface. A caregiver
 * who pressed Snooze or Stop and got no result must be told ON THE ALARM
 * SURFACE, persistently, with a way to try again. Reverting to a normal-looking
 * live banner with no explanation is what made the original defect dangerous —
 * the operator believed six times that he had stopped an alarm.
 *
 * ⚠ IT ALWAYS CARRIES A CONTROL. `Retry` is not decoration: this notice can be
 * on screen while audio is sounding, and the standing invariant is that at
 * least one alarm control is present in any state where audio can sound. There
 * is deliberately NO bare dismiss — dismissing without re-attempting would put
 * the surface straight back into the state this cycle abolishes.
 *
 * ⚠ NO PHI. The copy comes from `ackFailureMessage`, which names the control
 * and the outcome and nothing else — no member, no glucose value, no contact,
 * no token. This renders on a lock screen.
 */
import { ackFailureMessage } from "../../lib/ackRetry";
import type { AckFailure } from "../../lib/ackRetry";

export interface AckFailureNoticeProps {
  failure: AckFailure | null;
  onRetry: () => void;
  /** Compact layout for the mobile banner strip. */
  compact?: boolean;
}

export default function AckFailureNotice({
  failure,
  onRetry,
  compact = false,
}: AckFailureNoticeProps) {
  if (failure == null) return null;
  return (
    <div
      role="alert"
      aria-live="assertive"
      data-testid="ack-failure-notice"
      // Cycle 452.2: an inert marker — the type-floor sweep measures every
      // alarm control's buttons (44 px) and text (4.5:1) wherever it renders.
      data-alarm-control="ack-notice"
      style={{
        display: "flex",
        alignItems: "center",
        gap: compact ? 8 : 12,
        flexWrap: "nowrap",
        minWidth: 0,
        padding: compact ? "6px 10px" : "8px 14px",
        borderRadius: 10,
        // Token-free surfaces (the banners paint their own ground), so this
        // uses color-mix over currentColor rather than a hardcoded hex —
        // it reads correctly on the amber high banner and the red low one.
        // Cycle 452.2: 22% black left the white copy at about 2.6:1 over
        // amber. 55% makes the backing dark enough for 4.5:1 on EVERY alarm
        // ground it sits on — amber (about 7.5:1), both reds, the urgent
        // takeover's coral — in every theme.
        background: "color-mix(in srgb, #000 55%, transparent)",
        border: "1px solid color-mix(in srgb, #fff 45%, transparent)",
        color: "#fff",
        fontSize: 13,
        fontWeight: 600,
        lineHeight: 1.25,
      }}
    >
      <span
        aria-hidden
        style={{ fontSize: compact ? 13 : 15, flexShrink: 0 }}
      >
        ⚠
      </span>
      <span
        style={{
          flex: "1 1 auto",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {ackFailureMessage(failure)}
      </span>
      <button
        type="button"
        onClick={onRetry}
        data-testid="ack-failure-retry"
        style={{
          appearance: "none",
          border: 0,
          borderRadius: 8,
          background: "#fff",
          color: "#111",
          fontWeight: 800,
          fontSize: 13,
          // 44px minimum touch target — the kiosk sizing rule, and this can
          // be the only control on screen.
          minHeight: 44,
          minWidth: 72,
          padding: "0 14px",
          flexShrink: 0,
          cursor: "pointer",
        }}
      >
        Retry
      </button>
    </div>
  );
}
